import React, {useMemo, useState} from "react";
import {arrayMove} from "react-sortable-hoc";
import _ from "lodash";
import {toast} from "react-toastify";
import API from "../../helpers/Axios";
import SortableImages from "../../components/SortableImages";

const DEFAULT_RULES = [
	{title: "Suitable for children (2-12 years)"},
	{title: "Pet friendly"},
	{title: "Smoking allowed"},
	{title: "Alcohol drinking allowed"},
];

const DEFAULT_EQUIPMENTS = [
	{title: "Speakers", cost: 0},
	{title: "Computer", cost: 0},
	{title: "Work Station", cost: 0},
];

const DEFAULT_AMENITIES = [
	{title: "Free wifi", cost: 0},
	{title: "Free parking", cost: 0},
	{title: "Kitchen area", cost: 0},
];

const sanitizeMoney = (text) => {
	let val = String(text || "");
	val = val.replace(/([^0-9.]+)/g, "");
	val = val.replace(/^(0|\.)/, "");
	const match = /(\d{0,7})[^.]*((?:\.\d{0,2})?)/g.exec(val);
	if (!match) return "";
	return match[1] + match[2];
};

function NewRoomScreen({navigation, route}) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [roomArtworkFiles, setRoomArtworkFiles] = useState([]);

	const [roomName, setRoomName] = useState("");
	const [roomDescription, setRoomDescription] = useState("");
	const [roomPrice, setRoomPrice] = useState("");
	const [errors, setErrors] = useState({name: "", description: "", price: ""});

	const [roomRules, setRoomRules] = useState(DEFAULT_RULES);
	const [roomEquipments, setRoomEquipments] = useState(DEFAULT_EQUIPMENTS);
	const [roomAmenities, setRoomAmenities] = useState(DEFAULT_AMENITIES);

	const [additionRule, setAdditionRule] = useState("");
	const [additionEquipment, setAdditionEquipment] = useState("");
	const [additionEquipmentPrice, setAdditionEquipmentPrice] = useState("");
	const [additionAmenity, setAdditionAmenity] = useState("");
	const [additionAmenityPrice, setAdditionAmenityPrice] = useState("");

	const studioItem = route?.params?.item;
	const studioId = studioItem?.id;

	const helperTitle = useMemo(() => {
		if (!studioItem?.name) return "Create a room";
		return `Create room for ${studioItem.name}`;
	}, [studioItem]);

	const onFilesSelected = (event) => {
		const files = Array.from(event.target.files || []);
		if (!files.length) return;
		setRoomArtworkFiles((prev) => [...prev, ...files]);
		event.target.value = "";
	};

	const removeByIndex = (setter) => (index) => {
		setter((prev) => prev.filter((_, i) => i !== index));
	};

	const validateStepOne = async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);
		setErrors({name: "", description: "", price: ""});
		try {
			await API.post("owner/room-validate", {
				name: roomName,
				description: roomDescription,
				price: roomPrice,
				rules: roomRules,
				equipments: roomEquipments,
				amenities: roomAmenities,
			});
			setStepIndex(1);
		} catch (error) {
			const apiErrors = error?.response?.data?.errors || {};
			setErrors({
				name: apiErrors?.name?.[0] || "",
				description: apiErrors?.description?.[0] || "",
				price: apiErrors?.price?.[0] || "",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const createRoom = async () => {
		if (isSubmitting) return;
		if (!studioId) {
			toast.error("Studio is missing. Please open Add Room from a studio.");
			return;
		}
		if (!roomArtworkFiles.length) {
			toast.error("Please upload images");
			return;
		}

		setIsSubmitting(true);
		try {
			const data = new FormData();
			data.append("studio_id", studioId);
			data.append("room_name", roomName);
			data.append("room_price", roomPrice);
			data.append("room_description", roomDescription);

			roomRules.forEach((item) => data.append("rules[]", JSON.stringify(item)));
			roomAmenities.forEach((item) => data.append("amenities[]", JSON.stringify(item)));
			roomEquipments.forEach((item) => data.append("equipments[]", JSON.stringify(item)));
			roomArtworkFiles.forEach((file) => data.append("room_artworks[]", file));

			await API.post("owner/create-room", data);
			navigation.navigate("RoomsScreen");
		} catch (error) {
			const apiErrors = error?.response?.data?.errors || {};
			const removeIndex = [];
			Object.keys(apiErrors).forEach((key) => {
				if (key.includes("room_artworks")) {
					removeIndex.push(parseInt(key.replace(/^\D+/g, ""), 10));
				}
			});

			if (removeIndex.length > 0) {
				const files = [...roomArtworkFiles];
				_.pullAt(files, removeIndex);
				setRoomArtworkFiles(files);
				toast.error("Some uploaded images were rejected and removed. Please upload others.");
			} else {
				toast.error("Unable to create room.");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const renderListEditor = (title, items, setItems, adderTitle, adderName, adderCost, tipText) => (
		<div className="mt-10">
			<h3 className="text-2xl font-medium text-[#6D6E6F]">{title}</h3>
			<div className="mt-5 space-y-2">
				{items.map((item, index) => (
					<div key={`${item.title}-${index}`} className="flex items-center justify-between rounded-md px-1 py-2">
						<div className="flex items-center">
							<svg width={20} height={20} fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M17.256 4.41a.833.833 0 0 1 0 1.179l-9.167 9.167a.833.833 0 0 1-1.178 0L2.744 10.59a.833.833 0 1 1 1.178-1.179L7.5 12.989l8.578-8.578a.833.833 0 0 1 1.178 0Z"
									fill="#3DBA71"
								/>
							</svg>
							<span className="ml-3 font-semibold text-[#6D6E6F]">{item.title}</span>
						</div>
						<div className="flex items-center gap-3">
							<span className="font-medium text-[#178545]">{item.cost ? `$ ${item.cost}` : "Free"}</span>
							<button type="button" onClick={() => removeByIndex(setItems)(index)}>
								<svg width={20} height={20} fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="m5 5 10 10M15 5 5 15" stroke="#4F4F4F" strokeWidth={1.8} strokeLinecap="round" />
								</svg>
							</button>
						</div>
					</div>
				))}
			</div>

			<div className="mt-8 w-full max-w-[760px]">
				<p className="text-xl font-medium text-[#6D6E6F]">{adderTitle}</p>
				<div className="mt-4 flex h-14 overflow-hidden rounded-lg border border-[#D2D2D2]">
					<input
						placeholder={adderName}
						value={adderCost === null ? additionRule : adderCost === "equipment" ? additionEquipment : additionAmenity}
						onChange={(e) => {
							if (adderCost === null) setAdditionRule(e.target.value);
							if (adderCost === "equipment") setAdditionEquipment(e.target.value);
							if (adderCost === "amenity") setAdditionAmenity(e.target.value);
						}}
						className="flex-1 px-4 text-base outline-none"
					/>
					{adderCost !== null ? (
						<input
							placeholder="Additional cost: $ 0.00"
							value={adderCost === "equipment" ? additionEquipmentPrice : additionAmenityPrice}
							onChange={(e) => {
								const value = sanitizeMoney(e.target.value);
								if (adderCost === "equipment") setAdditionEquipmentPrice(value);
								if (adderCost === "amenity") setAdditionAmenityPrice(value);
							}}
							className="w-1/2 border-l border-[#AFABAC] px-4 text-base outline-none"
						/>
					) : null}
					<button
						type="button"
						onClick={() => {
							if (adderCost === null && additionRule.trim()) {
								setRoomRules((prev) => [...prev, {title: additionRule.trim()}]);
								setAdditionRule("");
							}
							if (adderCost === "equipment" && additionEquipment.trim() && additionEquipmentPrice.trim()) {
								setRoomEquipments((prev) => [...prev, {title: additionEquipment.trim(), cost: additionEquipmentPrice.trim()}]);
								setAdditionEquipment("");
								setAdditionEquipmentPrice("");
							}
							if (adderCost === "amenity" && additionAmenity.trim() && additionAmenityPrice.trim()) {
								setRoomAmenities((prev) => [...prev, {title: additionAmenity.trim(), cost: additionAmenityPrice.trim()}]);
								setAdditionAmenity("");
								setAdditionAmenityPrice("");
							}
						}}
						className="bg-[rgba(61,186,113,0.2)] px-6 font-semibold text-[#178545]"
					>
						Add
					</button>
				</div>
				<div className="mt-4 bg-[#F5F4F3] p-2 text-[#6D6E6F]">{tipText}</div>
			</div>
		</div>
	);

	return (
		<div className="p-6 md:p-10">
			<div className="mx-auto w-full max-w-[1240px]">
				<h1 className="text-3xl font-semibold md:text-[34px]">Let&apos;s create new Studio&apos;s Room!</h1>
				<p className="mt-4 text-lg text-[#6D6E6F] md:text-xl">
					Here we will finish filling out the information you need create new studio&apos;s room!
				</p>
				{!studioId ? (
					<div className="mt-5 rounded-md border border-[#F5C2C7] bg-[#FBEAEC] px-4 py-3 text-sm text-[#842029]">
						Studio context missing. Open this page from Rooms screen &quot;Add Room&quot; action.
					</div>
				) : null}

				<div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
					<div className="rounded-lg bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.08)]">
						<div className="mb-6 flex items-center justify-between border-b border-[#E4E1E0] pb-4">
							<div>
								<p className="text-sm font-semibold text-[#096730]">Step {stepIndex + 1} / 2</p>
								<p className="mt-1 text-xl font-semibold text-[#1E1F21]">{stepIndex === 0 ? "Room info and rules" : "Room photos"}</p>
							</div>
							<p className="text-sm font-medium text-[#6D6E6F]">{helperTitle}</p>
						</div>

						{stepIndex === 0 ? (
							<>
								<h3 className="text-2xl font-medium text-[#6D6E6F]">Room info</h3>
								<div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
									<div>
										<input
											placeholder="Room name"
											value={roomName}
											onChange={(e) => {
												setRoomName(e.target.value);
												setErrors((prev) => ({...prev, name: ""}));
											}}
											className={`h-12 w-full rounded-md border px-4 outline-none ${errors.name ? "border-[#DC3545]" : "border-[#DADADA] focus:border-[#31A762]"}`}
										/>
										{errors.name ? <p className="mt-2 text-sm text-[#DC3545]">{errors.name}</p> : null}
									</div>
									<div>
										<input
											placeholder="Price per hour"
											value={roomPrice}
											onChange={(e) => {
												setRoomPrice(sanitizeMoney(e.target.value));
												setErrors((prev) => ({...prev, price: ""}));
											}}
											className={`h-12 w-full rounded-md border px-4 outline-none ${errors.price ? "border-[#DC3545]" : "border-[#DADADA] focus:border-[#31A762]"}`}
										/>
										{errors.price ? <p className="mt-2 text-sm text-[#DC3545]">{errors.price}</p> : null}
									</div>
								</div>

								<div className="mt-10">
									<textarea
										placeholder="Description"
										value={roomDescription}
										maxLength={300}
										onChange={(e) => {
											setRoomDescription(e.target.value);
											setErrors((prev) => ({...prev, description: ""}));
										}}
										rows={4}
										className={`w-full rounded-md border px-4 py-3 outline-none ${errors.description ? "border-[#DC3545]" : "border-[#DADADA] focus:border-[#31A762]"}`}
									/>
									<div className="mt-2 flex justify-between text-sm text-[#4A535E]">
										{errors.description ? <p className="text-[#DC3545]">{errors.description}</p> : <span />}
										<p>{roomDescription.length}/300</p>
									</div>
								</div>

								<div className="mt-10">
									<h3 className="text-2xl font-medium text-[#6D6E6F]">Room rules</h3>
									<div className="mt-5 space-y-2">
										{roomRules.map((item, index) => (
											<div key={`${item.title}-${index}`} className="flex items-center justify-between py-2">
												<span className="font-semibold text-[#6D6E6F]">{item.title}</span>
												<button type="button" onClick={() => removeByIndex(setRoomRules)(index)}>
													<svg width={20} height={20} fill="none" xmlns="http://www.w3.org/2000/svg">
														<path d="m5 5 10 10M15 5 5 15" stroke="#4F4F4F" strokeWidth={1.8} strokeLinecap="round" />
													</svg>
												</button>
											</div>
										))}
									</div>
								</div>

								<div className="mt-10 w-full max-w-[760px]">
									<p className="text-xl font-medium text-[#6D6E6F]">Additional rules</p>
									<div className="mt-4 flex h-14 overflow-hidden rounded-lg border border-[#D2D2D2]">
										<input
											placeholder="Add additional rule"
											value={additionRule}
											onChange={(e) => setAdditionRule(e.target.value)}
											className="flex-1 px-4 text-base outline-none"
										/>
										<button
											type="button"
											onClick={() => {
												if (additionRule.trim()) {
													setRoomRules((prev) => [...prev, {title: additionRule.trim()}]);
													setAdditionRule("");
												}
											}}
											className="bg-[rgba(61,186,113,0.2)] px-6 font-semibold text-[#178545]"
										>
											Add
										</button>
									</div>
									<div className="mt-4 bg-[#F5F4F3] p-2 text-[#6D6E6F]">
										Additional rules are pending of approval.
									</div>
								</div>
								{renderListEditor(
									"Equipments",
									roomEquipments,
									setRoomEquipments,
									"Add Equipment",
									"Add additional equipment",
									"equipment",
									"Please note that Equipment can be free or charged per hour."
								)}
								{renderListEditor(
									"Amenities",
									roomAmenities,
									setRoomAmenities,
									"Add Amenity",
									"Add additional amenity",
									"amenity",
									"Please note that amenities can be free or charged per hour."
								)}

								<div className="mt-12 flex justify-end">
									<button
										type="button"
										onClick={validateStepOne}
										disabled={isSubmitting}
										className="inline-flex h-12 items-center rounded-md bg-[#3DBA71] px-6 font-medium text-white disabled:opacity-60"
									>
										<span>Next</span>
										{isSubmitting ? <span className="ml-3">...</span> : <span className="ml-3">→</span>}
									</button>
								</div>
							</>
						) : (
							<>
								<h3 className="text-2xl font-medium text-[#1E1F21]">Room Photos</h3>
								<p className="mt-3 leading-6 text-[#6D6E6F]">
									Take photos with your phone or a camera. Upload at least one photo to publish your studio room. You can drag and reorder them.
								</p>

								{roomArtworkFiles.length > 0 ? (
									<div className="mt-8">
										<SortableImages
											items={roomArtworkFiles}
											onSortEnd={({oldIndex, newIndex}) => {
												setRoomArtworkFiles((prev) => arrayMove([...prev], oldIndex, newIndex));
											}}
										/>
									</div>
								) : (
									<div className="mt-8 rounded-lg border border-[#D2D2D2] p-4">
										<div className="grid grid-cols-3 gap-3 opacity-20">
											{Array.from({length: 6}).map((_, idx) => (
												<div key={idx} className="aspect-[16/9] bg-[#97DEB4]" />
											))}
										</div>
										<div className="mt-4 flex justify-center">
											<label className="cursor-pointer rounded-md bg-[#3DBA71] px-6 py-3 font-medium text-white">
												Upload your photos
												<input type="file" accept="image/*" multiple onChange={onFilesSelected} className="hidden" />
											</label>
										</div>
									</div>
								)}

								<div className="mt-4">
									<label className="inline-flex cursor-pointer items-center rounded-md border border-[#E4E1E0] bg-white px-4 py-2 text-sm font-semibold text-[#1E1F21] hover:bg-[#F8F8F8]">
										Add more files
										<input type="file" accept="image/*" multiple onChange={onFilesSelected} className="hidden" />
									</label>
								</div>

								<div className="mt-8 bg-[#F5F4F3] p-2 text-[#6D6E6F]">You can upload up to X photos and one video.</div>

								<div className="mt-12 flex items-center justify-between">
									<button
										type="button"
										onClick={() => setStepIndex(0)}
										className="h-12 rounded-md border border-[#6D6E6F] px-6 font-medium text-[#6D6E6F]"
									>
										Previous
									</button>
									<button
										type="button"
										onClick={createRoom}
										disabled={isSubmitting || !studioId}
										className="inline-flex h-12 items-center rounded-md bg-[#3DBA71] px-6 font-medium text-white disabled:opacity-60"
									>
										<span>Create Room</span>
										{isSubmitting ? <span className="ml-3">...</span> : null}
									</button>
								</div>
							</>
						)}
					</div>

					<aside className="rounded-lg bg-white p-5 shadow-[0_2px_10px_rgba(0,0,0,0.08)]">
						<h3 className="text-lg font-semibold text-[#1E1F21]">Stugo tips</h3>
						<p className="mt-3 text-sm leading-6 text-[#6D6E6F]">
							Be short and concise in your description. Catch attention with clear and useful details about what your room offers.
						</p>
					</aside>
				</div>
			</div>
		</div>
	);
}

export default NewRoomScreen;
